import { computed } from 'vue';
import { defaultSettings, useOSettings } from './use-settings';
import { useOUserSettingsStore } from '../stores/use-user-settings-store';
import {
  ADMINISTRATION_DISABLED_SETTING_KEY,
  MULTITENANT_ENVIRONMENT_SETTING_KEY,
  createAdministrationPolicy,
  isAdminLinkAvailable,
  isAdminRouteAvailable,
  isApiPathAvailable,
} from '../utils/administration-features';

/**
 * The signed-in user's roles. Outside an app with Pinia, for example in a
 * unit test, there is no user and therefore no platform authority.
 */
function currentUserRoles(): unknown {
  try {
    return useOUserSettingsStore().oUserSettings?.role;
  } catch {
    return undefined;
  }
}

/**
 * The current policy from the merged settings and the signed-in user. Usable
 * outside components, for example in navigation guards and request preconditions.
 */
export function getAdministrationPolicy() {
  return createAdministrationPolicy(
    defaultSettings[ADMINISTRATION_DISABLED_SETTING_KEY],
    defaultSettings[MULTITENANT_ENVIRONMENT_SETTING_KEY],
    currentUserRoles(),
  );
}

/**
 * Reactive availability of administration pages and Hub API endpoints, from
 * `administration_disabled`, `multitenant_environment` and whether the user has
 * `domainadmin`. The admin role check itself stays with the admin guard.
 */
export function useAdministrationFeatures() {
  const settings = useOSettings();

  const policy = computed(() =>
    createAdministrationPolicy(
      settings[ADMINISTRATION_DISABLED_SETTING_KEY]?.value,
      settings[MULTITENANT_ENVIRONMENT_SETTING_KEY]?.value,
      currentUserRoles(),
    ),
  );

  return {
    policy,
    disabledFeatures: computed(() => policy.value.disabledFeatures),
    multitenant: computed(() => policy.value.multitenant),
    platformAuthority: computed(() => policy.value.platformAuthority === true),
    isAdminRouteAvailable: (route: { name?: unknown; path?: string }) => isAdminRouteAvailable(route, policy.value),
    isAdminLinkAvailable: (link: string | undefined | null) => isAdminLinkAvailable(link, policy.value),
    isApiPathAvailable: (url: string | undefined | null) => isApiPathAvailable(url, policy.value),
  };
}
